/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChild, Fragment } from 'preact';
import { forwardRef, ForwardedRef, ElementRef } from 'preact/compat';

import { BaseButton } from '#UNSAFE_BaseButton';
import { ToggleDetail } from '#hooks/UNSAFE_useToggleAction';
import { useTooltip } from '#hooks/UNSAFE_useTooltip';
import { TestIdProps } from '#hooks/UNSAFE_useTestId';
import type { DimensionProps } from '#utils/UNSAFE_interpolations/dimensions';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { useButtonSetContext } from '#hooks/UNSAFE_useButtonSetContext';
import { ToggleButtonLabel } from '#UNSAFE_ToggleButtonLabel';
import {
  toggleStyling,
  processSetButtonProps,
  ButtonSetPosition,
  LayoutWidths,
  type InputTypes
} from '#utils/UNSAFE_buttonUtils';

type ButtonHandle = ElementRef<typeof BaseButton>;

type WidthInterpolationProps = Pick<DimensionProps, 'width'>;

export type ButtonSetIconButtonProps = WidthInterpolationProps &
  TestIdProps & {
    /**
     * icon - the icon for the button
     */
    children?: ComponentChild;

    /**
     * InpuType indicate if the toggle is a radio or checkbox,
     */
    inputType?: InputTypes;

    /**
     * The name of the input group
     */
    inputName?: string;

    /**
     * The value of the input
     */
    value?: string;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * The ID of an element (or space separated IDs of multiple elements) that
     * describes the button.
     */
    'aria-describedby'?: string;

    /**
     * Specifies if the toggle button is selected
     */
    isSelected?: boolean;

    /**
     * Property that triggers a callback immediately when toggle happens and value of isSelected property should be updated
     */
    onToggle?: (details: ToggleDetail) => void;

    /**
     * Indicates in what states the button has chrome (background and border).
     *     "ghost": "Ghost buttons are the least prominent variant. Ghost buttons are
     * useful for performing low-priority tasks, such as manipulating the UI.",
     *     "borderless"': "Borderless buttons are a more prominent variation. Borderless
     * buttons are useful for supplemental actions that require minimal emphasis.",
     *     "outlined"': "Outlined buttons are salient, but lighter weight than
     * solid buttons. Outlined buttons are useful for secondary actions.",
     */
    variant?: 'ghost' | 'borderless' | 'outlined';

    /**
     * The size indicates how large the button is rendered: "sm", "md" (default), "lg".
     */
    size?: 'sm' | 'md' | 'lg';

    /**
     * The position modifies the borders for usage inside a button group.
     */
    position?: ButtonSetPosition;

    /**
     * The layoutWidth specifies if the toggle button width fits the contents or
     * the container.
     */
    layoutWidth?: LayoutWidths;
  };

/**
 * Note that either an accessibleLabel or tooltip must be specified.
 */
type VariationAccessibleLabelProps =
  | {
      /**
       * accessibleLabel - the aria label
       */
      accessibleLabel: string;

      /**
       * tooltip - overrides the accessibleLabel for the tooltip
       */
      tooltip?: string;
    }
  | {
      accessibleLabel?: string;

      tooltip: string;
    };

/**
 * A ButtonSetIconButton is an icon button that allows users to switch
 * between states when pressed.
 */
export const ButtonSetIconButton = forwardRef(
  (
    {
      children,
      tooltip,
      value,
      accessibleLabel,
      isSelected = false,
      onToggle,
      position,
      testId,
      width,
      'aria-describedby': ariaDescribedBy,
      ...props
    }: ButtonSetIconButtonProps & VariationAccessibleLabelProps & TestIdProps,
    ref: ForwardedRef<ButtonHandle>
  ) => {
    const buttonSetProps = useButtonSetContext();
    const { inputProps, buttonSetButtonProps, isDisabled } = processSetButtonProps(
      props,
      buttonSetProps
    );

    const { tooltipContent, tooltipProps } = useTooltip({
      text: tooltip ?? accessibleLabel,
      isDisabled: isDisabled
    });

    const styling = toggleStyling(
      buttonSetButtonProps.layoutWidth,
      isSelected,
      false,
      true,
      buttonSetButtonProps.variant === 'outlined',
      position,
      undefined,
      true
    );

    const ariaProps = { 'aria-describedby': ariaDescribedBy };

    return (
      <Fragment>
        <BaseButton
          {...mergeProps(buttonSetButtonProps, ariaProps, tooltipProps)}
          elementDetails={{ type: 'span', isFocusable: false, isLabel: true }}
          onAction={() => onToggle?.({ value: isSelected })}
          styling={styling}
          width={buttonSetProps.width ? '100%' : width}
          ref={ref}
          testId={testId}>
          <ToggleButtonLabel
            label={accessibleLabel ?? tooltip}
            display={'icons'}
            isSelected={isSelected}
            {...inputProps}
            value={value}
            startIcon={children}></ToggleButtonLabel>
        </BaseButton>
        {tooltipContent}
      </Fragment>
    );
  }
);

ButtonSetIconButton.displayName = 'ButtonSetIconButton';
